import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import LicenceService from '../services/licenceService';

const emptyPlugin = {
  plugin_name: '',
  plugin_slug: '',
  plugin_description: '',
  is_active: true,
  feature_catalog: [],
};

function slugify(value) {
  return value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function toForm(plugin) {
  return {
    plugin_name: plugin.plugin_name || '',
    plugin_slug: plugin.plugin_slug || '',
    plugin_description: plugin.plugin_description || '',
    is_active: plugin.is_active !== false,
    feature_catalog: (plugin.feature_catalog || []).map((item) => ({
      key: item.key || '',
      label: item.label || '',
    })),
  };
}

const PluginManagerDialog = ({ open, onClose, onSaved }) => {
  const [plugins, setPlugins] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [form, setForm] = useState(emptyPlugin);
  const [slugEdited, setSlugEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const loadPlugins = async () => {
    const result = await LicenceService.getProducts({ include_inactive: true });
    if (result.success) {
      setPlugins(result.data);
    } else {
      setError(result.error);
    }
  };

  useEffect(() => {
    if (!open) return;
    setSelectedId(null);
    setForm(emptyPlugin);
    setSlugEdited(false);
    setError('');
    loadPlugins();
  }, [open]);

  const startCreate = () => {
    setSelectedId(null);
    setForm(emptyPlugin);
    setSlugEdited(false);
    setError('');
  };

  const startEdit = (plugin) => {
    setSelectedId(plugin.id);
    setForm(toForm(plugin));
    setSlugEdited(true);
    setError('');
  };

  const updateForm = (name, value) => {
    setForm((current) => {
      const next = { ...current, [name]: value };
      if (name === 'plugin_name' && !slugEdited && !selectedId) {
        next.plugin_slug = slugify(value);
      }
      return next;
    });
  };

  const updateCatalog = (index, field, value) => {
    setForm((current) => {
      const featureCatalog = current.feature_catalog.map((item, itemIndex) => (
        itemIndex === index ? { ...item, [field]: value } : item
      ));
      return { ...current, feature_catalog: featureCatalog };
    });
  };

  const handleSave = async () => {
    if (!form.plugin_name.trim() || !form.plugin_slug.trim()) {
      setError('Συμπλήρωσε όνομα και slug');
      return;
    }
    setSaving(true);
    setError('');
    const payload = {
      plugin_name: form.plugin_name.trim(),
      plugin_slug: form.plugin_slug.trim(),
      plugin_description: form.plugin_description,
      is_active: form.is_active,
      feature_catalog: form.feature_catalog
        .filter((item) => item.key.trim())
        .map((item) => ({ key: item.key.trim(), label: item.label.trim() || item.key.trim() })),
    };
    const result = await LicenceService.saveProduct(payload, selectedId);
    setSaving(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    await loadPlugins();
    startEdit(result.data);
    if (onSaved) onSaved();
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    const plugin = plugins.find((item) => item.id === selectedId);
    const confirmed = window.confirm(`Να σβηστεί το ${plugin?.plugin_name || 'plugin'};`);
    if (!confirmed) return;
    setSaving(true);
    setError('');
    const result = await LicenceService.deleteProduct(selectedId);
    setSaving(false);
    if (!result.success) {
      setError(result.error);
      return;
    }
    startCreate();
    await loadPlugins();
    if (onSaved) onSaved();
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Plugins</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} md={4}>
            <Button startIcon={<AddIcon />} onClick={startCreate} sx={{ mb: 1 }}>
              Νέο plugin
            </Button>
            <List dense>
              {plugins.map((plugin) => (
                <ListItemButton
                  key={plugin.id}
                  selected={plugin.id === selectedId}
                  onClick={() => startEdit(plugin)}
                >
                  <ListItemText
                    primary={plugin.plugin_name}
                    secondary={plugin.is_active ? plugin.plugin_slug : 'Ανενεργό'}
                  />
                </ListItemButton>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={8}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Όνομα"
                  value={form.plugin_name}
                  onChange={(event) => updateForm('plugin_name', event.target.value)}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="Slug"
                  value={form.plugin_slug}
                  onChange={(event) => {
                    setSlugEdited(true);
                    updateForm('plugin_slug', event.target.value);
                  }}
                  helperText="Αυτό στέλνει το plugin στον έλεγχο. Μην το αλλάζεις σε υπάρχον plugin."
                />
              </Grid>
              <Grid item xs={12}>
                <FormControlLabel
                  control={(
                    <Switch
                      checked={form.is_active}
                      onChange={(event) => updateForm('is_active', event.target.checked)}
                    />
                  )}
                  label="Ενεργό"
                />
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle2">Διακόπτες</Typography>
                  <Button
                    size="small"
                    onClick={() => setForm((current) => ({
                      ...current,
                      feature_catalog: [...current.feature_catalog, { key: '', label: '' }],
                    }))}
                  >
                    Προσθήκη
                  </Button>
                </Box>
                {form.feature_catalog.map((item, index) => (
                  <Box key={`feature-${index}`} sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <TextField
                      size="small"
                      label="Key"
                      value={item.key}
                      onChange={(event) => updateCatalog(index, 'key', event.target.value)}
                    />
                    <TextField
                      size="small"
                      fullWidth
                      label="Ετικέτα"
                      value={item.label}
                      onChange={(event) => updateCatalog(index, 'label', event.target.value)}
                    />
                    <IconButton
                      onClick={() => setForm((current) => ({
                        ...current,
                        feature_catalog: current.feature_catalog.filter((_, itemIndex) => itemIndex !== index),
                      }))}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Grid>
              {error && (
                <Grid item xs={12}>
                  <Typography color="error" variant="body2">{error}</Typography>
                </Grid>
              )}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
        <Box>
          {selectedId && (
            <Button color="error" onClick={handleDelete} disabled={saving}>
              Διαγραφή
            </Button>
          )}
        </Box>
        <Box>
        <Button onClick={onClose} disabled={saving}>Κλείσιμο</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Αποθήκευση...' : 'Αποθήκευση'}
        </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PluginManagerDialog;
